import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Box, TextField, Button, Alert, Divider } from '@mui/material';
import { changePassword } from '../api/client';

const ProfilePage = () => {
    const [user, setUser] = useState(null);
    const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
    const [message, setMessage] = useState(null); // { type: 'success'|'error', text: '' }
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setMessage(null);

        if (passwords.new !== passwords.confirm) {
            setMessage({ type: 'error', text: "New passwords don't match" });
            return;
        }

        setLoading(true);
        try {
            await changePassword(passwords.current, passwords.new);
            setMessage({ type: 'success', text: 'Password changed successfully!' });
            setPasswords({ current: '', new: '', confirm: '' });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <Container sx={{ py: 8, textAlign: 'center' }}>
                <Typography>Please log in to view your profile.</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                My Profile
            </Typography>

            <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 4 }}>
                <Typography variant="h6" gutterBottom>Account Details</Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                    <Typography variant="body1">{user.name}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{user.email}</Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">Role</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{user.role}</Typography>
                </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                <Typography variant="h6" gutterBottom>Security</Typography>
                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>Change Password</Typography>

                {message && (
                    <Alert severity={message.type} sx={{ mb: 3 }}>
                        {message.text}
                    </Alert>
                )}

                <form onSubmit={handleChangePassword}>
                    <TextField
                        fullWidth label="Current Password" type="password"
                        value={passwords.current}
                        onChange={e => setPasswords({ ...passwords, current: e.target.value })}
                        required margin="normal" sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth label="New Password" type="password"
                            value={passwords.new}
                            onChange={e => setPasswords({ ...passwords, new: e.target.value })}
                            required margin="normal"
                        />
                        <TextField
                            fullWidth label="Confirm New Password" type="password"
                            value={passwords.confirm}
                            onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                            required margin="normal"
                        />
                    </Box>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="submit" variant="contained"
                            disabled={loading}
                        >
                            {loading ? 'Updating...' : 'Update Password'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Container>
    );
};

export default ProfilePage;
